import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import clsx from "clsx";
import type { BoqPage, BoqPosition } from "./types";

export const AllPositionsTable = ({
  positions,
  currentPage,
  onNavigateToPage,
}: {
  positions: BoqPosition[];
  currentPage: BoqPage | null;
  onNavigateToPage: (file: string, page: number) => void;
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    if (!search) return positions;
    const q = search.toLowerCase();
    return positions.filter(
      (p) =>
        p.position_number?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) ||
        p.section_name?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.manufacturer?.toLowerCase().includes(q),
    );
  }, [positions, search]);

  if (positions.length === 0) {
    return (
      <div className="text-center text-gray-400 py-12 text-sm">
        {t("smartSurveyor.researchReport.noBoqPositions")}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Search */}
      <div className="relative">
        <Search
          size={14}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("smartSurveyor.reviewPage.searchPlaceholder")}
          className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] font-medium text-gray-400 uppercase tracking-wider bg-gray-50/80 border-b border-gray-100">
                <th className="px-4 py-2.5">Pos.</th>
                <th className="px-4 py-2.5">
                  {t("smartSurveyor.researchReport.posHeaders.description")}
                </th>
                <th className="px-4 py-2.5 text-right">
                  {t("smartSurveyor.researchReport.posHeaders.qty")}
                </th>
                <th className="px-4 py-2.5">
                  {t("smartSurveyor.researchReport.posHeaders.unit")}
                </th>
                <th className="px-4 py-2.5">
                  {t("smartSurveyor.researchReport.posHeaders.section")}
                </th>
                <th className="px-4 py-2.5">
                  {t("smartSurveyor.researchReport.posHeaders.page")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((p, i) => {
                const isCurrentPage =
                  currentPage &&
                  p.file === currentPage.file &&
                  p.page === currentPage.page;
                return (
                  <tr
                    key={i}
                    className={clsx(
                      "hover:bg-gray-50/50 transition-colors",
                      isCurrentPage && "bg-blue-50/50",
                    )}
                  >
                    <td className="px-4 py-2.5 font-mono font-medium text-gray-700 whitespace-nowrap">
                      {p.position_number || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 max-w-[250px] truncate">
                      {(p.description || "").split("\n")[0]}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono font-medium text-gray-800 whitespace-nowrap">
                      {p.quantity ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500">
                      {p.unit || ""}
                    </td>
                    <td className="px-4 py-2.5 text-gray-400 truncate max-w-[120px]">
                      {p.section_name || p.section_number || ""}
                    </td>
                    <td className="px-4 py-2.5">
                      {p.page && p.file ? (
                        <button
                          type="button"
                          className="inline-flex items-center text-[10px] font-mono font-medium text-blue-600 bg-blue-50 border border-blue-100 rounded-md px-2 py-0.5 hover:bg-blue-100 hover:border-blue-200 transition-colors"
                          onClick={() => onNavigateToPage(p.file!, p.page!)}
                        >
                          p.{p.page}
                        </button>
                      ) : p.page ? (
                        <span className="text-gray-300 font-mono">
                          p.{p.page}
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Results count */}
      {search && (
        <div className="text-xs text-gray-400 text-center">
          {filtered.length} / {positions.length}
        </div>
      )}
    </div>
  );
};
